import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { FaCompress, FaExpand, FaPen } from 'react-icons/fa';
import { useInRouterContext, useNavigate } from 'react-router-dom';
import { useApi } from '../../state/ApiContext';
import { useTheme } from '../../state/ThemeContext';
import { useProjectSocket } from '../../hooks/useProjectSocket';
import { ChartConfig, ChartDataResponse } from '../../client';
import {
  CHART_OTHER_COLOR,
  chartChrome,
  chartSeriesColors,
} from './chart-theme';

interface ChartViewProps {
  teamId: number;
  projectId: number;
  /** databases/-relative path of the .chart.json. */
  path: string;
  height?: number;
  /** Re-fetch on database_file_changed for the chart's database (default). */
  live?: boolean;
  /** Hide the title bar (when the parent renders its own). */
  hideTitle?: boolean;
  /** Show an edit button that hands the loaded config to the parent. */
  onEdit?: (config: ChartConfig) => void;
}

const OTHER_KEY = '__other__';

const SUBTITLE_DESCRIPTION_LIMIT = 50;

/**
 * The chart's source database ("From <db> database.", a link to its browser
 * view) with the chart's description alongside, truncated. Wiki/card embeds
 * mount ChartView outside any Router, so SPA navigation is used only when a
 * router is present; otherwise the plain href does a full navigation.
 */
const DatabaseSubtitle: React.FC<{
  teamId: number;
  projectId: number;
  databasePath: string;
  /** The chart's SQL — the workbench prefills and runs it on arrival. */
  sql?: string;
  /** The chart's own path — lets the workbench offer "Update … Chart". */
  chartPath?: string;
  /** The chart's description, shown truncated after the database link. */
  description?: string;
}> = ({ teamId, projectId, databasePath, sql, chartPath, description }) => {
  const inRouter = useInRouterContext();
  const params = new URLSearchParams();
  if (sql) params.set('sql', sql);
  if (chartPath) params.set('chart', chartPath);
  const search = params.toString();
  const url =
    `/console/team/${teamId}/projects/${projectId}/databases/${encodeURIComponent(
      databasePath,
    )}` + (search ? `?${search}` : '');
  const label = `From ${databasePath.replace(/\.sqlite$/i, '')} database.`;
  const blurb = description?.trim()
    ? description.trim().length > SUBTITLE_DESCRIPTION_LIMIT
      ? `${description.trim().slice(0, SUBTITLE_DESCRIPTION_LIMIT).trimEnd()}…`
      : description.trim()
    : null;
  return (
    <span className="chart-view-subtitle-line">
      {inRouter ? (
        <RoutedDatabaseSubtitle url={url} label={label} />
      ) : (
        <a
          className="chart-view-subtitle"
          data-testid="chart-database-link"
          href={url}>
          {label}
        </a>
      )}
      {blurb && (
        <span
          className="chart-view-description"
          data-testid="chart-description"
          title={description}>
          {blurb}
        </span>
      )}
    </span>
  );
};

const RoutedDatabaseSubtitle: React.FC<{ url: string; label: string }> = ({
  url,
  label,
}) => {
  const navigate = useNavigate();
  return (
    <a
      className="chart-view-subtitle"
      data-testid="chart-database-link"
      href={url}
      onClick={(e) => {
        e.preventDefault();
        navigate(url);
        // Same jump as selecting the database in the sidebar
        document
          .querySelector('[data-testid="team-page-title"]')
          ?.scrollIntoView();
      }}>
      {label}
    </a>
  );
};

/** Recharts dataKeys can't contain dots (path syntax); use slot keys. */
function slotKey(index: number): string {
  return `s${index}`;
}

/**
 * The one chart renderer for the databases system: dashboards, wiki embeds,
 * and cards all render this component so charts look and behave identically
 * everywhere. Fetches `/databases/chart/data` (server-side read-only SQL with
 * caching), renders with recharts on the Harbor Ledger palette, live-updates
 * over the project socket, and expands to full screen.
 */
const ChartView: React.FC<ChartViewProps> = ({
  teamId,
  projectId,
  path,
  height = 280,
  live = true,
  hideTitle = false,
  onEdit,
}) => {
  const { databasesApi } = useApi();
  const { theme } = useTheme();
  const [data, setData] = useState<ChartDataResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res =
        await databasesApi.apiTeamsTeamIdProjectsProjectIdDatabasesChartDataGet(
          teamId,
          projectId,
          path,
        );
      setData(res.data);
      setError(null);
    } catch (err) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Failed to load chart';
      setError(message);
    }
  }, [databasesApi, teamId, projectId, path]);

  useEffect(() => {
    load();
  }, [load]);

  // Live: when this chart's database (or the chart config itself) changes,
  // re-fetch. The server cache is keyed per version, so this always gets
  // fresh data after a write and a cheap cache hit otherwise.
  useProjectSocket(live ? projectId : 0, (message) => {
    if (message.updateType !== 'database_file_changed') return;
    if (message.path === data?.config?.databasePath || message.path === path) {
      load();
    }
  });

  const body = data ? (
    <ChartBody
      data={data}
      theme={theme === 'dark' ? 'dark' : 'light'}
      height={fullscreen ? Math.max(320, window.innerHeight - 160) : height}
    />
  ) : error ? (
    <div className="chart-view-error">{error}</div>
  ) : (
    <div className="chart-view-loading">Loading chart…</div>
  );

  const titleBar = (
    <div className="chart-view-titlebar">
      {!hideTitle && (
        <span className="chart-view-heading">
          <span className="chart-view-title">
            {data?.config?.title ?? path}
          </span>
          {data?.config?.databasePath && (
            <DatabaseSubtitle
              teamId={teamId}
              projectId={projectId}
              databasePath={data.config.databasePath}
              sql={data.config.sql}
              chartPath={path}
              description={data.config.description}
            />
          )}
        </span>
      )}
      <span className="chart-view-actions">
        {onEdit && data?.config && (
          <button
            className="icon-button chart-view-expand"
            aria-label="Edit chart"
            title="Edit chart"
            data-testid="chart-edit-button"
            onClick={() => onEdit(data.config)}>
            <FaPen size={11} />
          </button>
        )}
        <button
          className="icon-button chart-view-expand"
          aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
          title={fullscreen ? 'Exit full screen' : 'Full screen'}
          onClick={() => setFullscreen((f) => !f)}>
          {fullscreen ? <FaCompress size={12} /> : <FaExpand size={12} />}
        </button>
      </span>
    </div>
  );

  if (fullscreen) {
    return (
      <div
        className="chart-view-fullscreen"
        onClick={() => setFullscreen(false)}>
        <div
          className="chart-view chart-view-fullscreen-inner"
          onClick={(e) => e.stopPropagation()}>
          {titleBar}
          {body}
        </div>
      </div>
    );
  }

  return (
    <div className="chart-view" data-testid="chart-view">
      {titleBar}
      {body}
    </div>
  );
};

interface ShapedData {
  rows: Array<Record<string, string | number | null>>;
  /** Plotted series in stable order: [dataKey, displayName]. */
  series: Array<{ dataKey: string; name: string }>;
  xKey: string;
}

/**
 * Shape columns+row-arrays into recharts row objects. The x column comes
 * from encoding.x (default: first column); plotted series come from
 * encoding.series/value (default: every other numeric column). Series
 * beyond the 8 palette slots fold into a summed "Other" — never a 9th hue.
 */
function shapeData(
  config: ChartConfig,
  columns: string[],
  rows: Array<Array<unknown>>,
  slots: number,
): ShapedData {
  const xColumn =
    config.encoding?.x && columns.includes(config.encoding.x)
      ? config.encoding.x
      : columns[0];
  const xIndex = Math.max(0, columns.indexOf(xColumn));

  let seriesColumns: string[] =
    config.encoding?.series?.filter((s) => columns.includes(s)) ?? [];
  if (seriesColumns.length === 0 && config.encoding?.value) {
    if (columns.includes(config.encoding.value)) {
      seriesColumns = [config.encoding.value];
    }
  }
  if (seriesColumns.length === 0) {
    // Default: every non-x column whose first non-null value is numeric.
    seriesColumns = columns.filter((column, index) => {
      if (index === xIndex) return false;
      const sample = rows.find((row) => row[index] !== null)?.[index];
      return typeof sample === 'number';
    });
  }

  const shown = seriesColumns.slice(0, slots);
  const folded = seriesColumns.slice(slots);
  const series = shown.map((name, index) => ({
    dataKey: slotKey(index),
    name,
  }));
  if (folded.length > 0) {
    series.push({ dataKey: OTHER_KEY, name: 'Other' });
  }

  const shaped = rows.map((row) => {
    const out: Record<string, string | number | null> = {
      x: row[xIndex] as string | number | null,
    };
    shown.forEach((column, index) => {
      const value = row[columns.indexOf(column)];
      out[slotKey(index)] = typeof value === 'number' ? value : null;
    });
    if (folded.length > 0) {
      out[OTHER_KEY] = folded.reduce((sum: number, column) => {
        const value = row[columns.indexOf(column)];
        return sum + (typeof value === 'number' ? value : 0);
      }, 0);
    }
    return out;
  });

  return { rows: shaped, series, xKey: 'x' };
}

const ChartBody: React.FC<{
  data: ChartDataResponse;
  theme: 'light' | 'dark';
  height: number;
}> = ({ data, theme, height }) => {
  const colors = chartSeriesColors(theme);
  const chrome = chartChrome(theme);
  const config = data.config;

  const shaped = useMemo(
    () =>
      shapeData(
        config,
        data.columns,
        data.rows as Array<Array<unknown>>,
        colors.length,
      ),
    [config, data.columns, data.rows, colors.length],
  );

  if (data.rows.length === 0) {
    return <div className="chart-view-empty">No data.</div>;
  }

  const colorFor = (entry: { dataKey: string }, index: number) =>
    entry.dataKey === OTHER_KEY ? CHART_OTHER_COLOR : colors[index];

  const axes = (
    <>
      <CartesianGrid vertical={false} stroke={chrome.grid} strokeWidth={1} />
      <XAxis
        dataKey={shaped.xKey}
        tick={{ fill: chrome.axisText, fontSize: 11 }}
        axisLine={false}
        tickLine={false}
        minTickGap={24}
      />
      <YAxis
        tick={{ fill: chrome.axisText, fontSize: 11 }}
        axisLine={false}
        tickLine={false}
        width={56}
      />
    </>
  );
  const tooltip = (
    <Tooltip
      cursor={{ fill: chrome.grid, opacity: 0.35 }}
      contentStyle={{
        background: 'var(--card-background)',
        border: '1px solid var(--border-color)',
        borderRadius: 6,
        fontSize: 12,
        color: 'var(--text-color)',
      }}
      labelStyle={{ color: 'var(--secondary-text-color)' }}
    />
  );
  // Every chart carries a legend (a deliberate product choice — even a
  // single series names itself rather than relying on the title alone).
  const legend = (
    <Legend
      iconType="circle"
      iconSize={8}
      formatter={(value) => (
        <span className="chart-view-legend-label">{value}</span>
      )}
    />
  );
  const margin = { top: 8, right: 8, bottom: 0, left: 8 };

  if (config.type === 'pie' || config.type === 'donut') {
    // One slice per row: x column is the label, first series the value.
    const valueKey = shaped.series[0]?.dataKey;
    if (!valueKey)
      return <div className="chart-view-empty">No numeric column to plot.</div>;
    const sliceRows = shaped.rows.slice(0, colors.length);
    const foldedTotal = shaped.rows
      .slice(colors.length)
      .reduce((sum, row) => sum + ((row[valueKey] as number) || 0), 0);
    const pieData = [
      ...sliceRows.map((row) => ({
        name: String(row[shaped.xKey] ?? ''),
        value: (row[valueKey] as number) || 0,
      })),
      ...(foldedTotal > 0 ? [{ name: 'Other', value: foldedTotal }] : []),
    ];
    return (
      <ResponsiveContainer width="100%" height={height}>
        <PieChart margin={margin}>
          {tooltip}
          {legend}
          <Pie
            data={pieData}
            dataKey="value"
            nameKey="name"
            innerRadius={config.type === 'donut' ? '55%' : 0}
            stroke={chrome.surface}
            strokeWidth={2}
            isAnimationActive={false}>
            {pieData.map((entry, index) => (
              <Cell
                key={entry.name}
                fill={
                  entry.name === 'Other' && index === pieData.length - 1
                    ? CHART_OTHER_COLOR
                    : colors[index % colors.length]
                }
              />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    );
  }

  if (config.type === 'radialbar') {
    // One arc per row, pie-style shaping: x is the label, first series the
    // value. Legend payload is built by hand — RadialBar has no nameKey.
    const valueKey = shaped.series[0]?.dataKey;
    if (!valueKey)
      return <div className="chart-view-empty">No numeric column to plot.</div>;
    const arcRows = shaped.rows.slice(0, colors.length);
    const radialData = arcRows.map((row, index) => ({
      name: String(row[shaped.xKey] ?? ''),
      value: (row[valueKey] as number) || 0,
      fill: colors[index % colors.length],
    }));
    return (
      <ResponsiveContainer width="100%" height={height}>
        <RadialBarChart
          data={radialData}
          innerRadius="20%"
          outerRadius="95%"
          margin={margin}>
          {tooltip}
          <Legend
            content={() => (
              <ul className="chart-view-custom-legend">
                {radialData.map((entry) => (
                  <li key={entry.name}>
                    <span
                      className="chart-view-legend-chip"
                      style={{ backgroundColor: entry.fill }}
                    />
                    <span className="chart-view-legend-label">
                      {entry.name}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          />
          <RadialBar
            dataKey="value"
            background={{ fill: chrome.grid }}
            isAnimationActive={false}
          />
        </RadialBarChart>
      </ResponsiveContainer>
    );
  }

  if (config.type === 'radar') {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <RadarChart data={shaped.rows} margin={margin}>
          <PolarGrid stroke={chrome.grid} />
          <PolarAngleAxis
            dataKey={shaped.xKey}
            tick={{ fill: chrome.axisText, fontSize: 11 }}
          />
          <PolarRadiusAxis
            tick={{ fill: chrome.axisText, fontSize: 10 }}
            axisLine={false}
          />
          {tooltip}
          {legend}
          {shaped.series.map((entry, index) => (
            <Radar
              key={entry.dataKey}
              dataKey={entry.dataKey}
              name={entry.name}
              stroke={colorFor(entry, index)}
              strokeWidth={2}
              fill={colorFor(entry, index)}
              fillOpacity={0.2}
              isAnimationActive={false}
            />
          ))}
        </RadarChart>
      </ResponsiveContainer>
    );
  }

  if (config.type === 'scatter') {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <ScatterChart margin={margin}>
          {axes}
          {tooltip}
          {legend}
          {shaped.series.map((entry, index) => (
            <Scatter
              key={entry.dataKey}
              name={entry.name}
              data={shaped.rows.map((row) => ({
                [shaped.xKey]: row[shaped.xKey],
                [entry.dataKey]: row[entry.dataKey],
              }))}
              dataKey={entry.dataKey}
              fill={colorFor(entry, index)}
              stroke={chrome.surface}
              strokeWidth={1}
              isAnimationActive={false}
            />
          ))}
        </ScatterChart>
      </ResponsiveContainer>
    );
  }

  if (config.type === 'line') {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={shaped.rows} margin={margin}>
          {axes}
          {tooltip}
          {legend}
          {shaped.series.map((entry, index) => (
            <Line
              key={entry.dataKey}
              type="monotone"
              dataKey={entry.dataKey}
              name={entry.name}
              stroke={colorFor(entry, index)}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, stroke: chrome.surface, strokeWidth: 2 }}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  }

  if (config.type === 'area') {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={shaped.rows} margin={margin}>
          {axes}
          {tooltip}
          {legend}
          {shaped.series.map((entry, index) => (
            <Area
              key={entry.dataKey}
              type="monotone"
              dataKey={entry.dataKey}
              name={entry.name}
              stackId="a"
              stroke={colorFor(entry, index)}
              strokeWidth={2}
              fill={colorFor(entry, index)}
              fillOpacity={0.25}
              isAnimationActive={false}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  // bar (default)
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={shaped.rows} margin={margin}>
        {axes}
        {tooltip}
        {legend}
        {shaped.series.map((entry, index) => (
          <Bar
            key={entry.dataKey}
            dataKey={entry.dataKey}
            name={entry.name}
            fill={colorFor(entry, index)}
            stroke={chrome.surface}
            strokeWidth={1}
            radius={shaped.series.length === 1 ? [4, 4, 0, 0] : undefined}
            maxBarSize={28}
            isAnimationActive={false}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default ChartView;
